import { Link } from "react-router-dom";
import { Facebook, Linkedin, Instagram } from "lucide-react";
import yootelcoLogo from "@/assets/yootelco-logo.png.asset.json";

const solutionsLinks = [
  { label: "A2P SMS", to: "/solutions/a2p-sms" },
  { label: "OTP Authentication", to: "/solutions/otp-authentication" },
  { label: "Voice Termination", to: "/solutions/voice-termination" },
  { label: "Transactional Alerts", to: "/use-cases/transactional-alerts" },
  { label: "Promotional SMS", to: "/use-cases/promotional-sms" },
  { label: "Payment & Fintech", to: "/use-cases/payment-fintech" },
];

const companyLinks: { label: string; to: string; external?: boolean }[] = [
  { label: "About", to: "/about" },
  { label: "Blog", to: "https://blog.yootelco.com/", external: true },
  { label: "Contact", to: "/contact" },
  { label: "Industries", to: "/#industries" },
  { label: "Coverage & Routes", to: "/#coverage-routes" },
];

const offices = [
  { country: "United Kingdom", address: "103 High Street, Waltham Cross, Hertfordshire EN8 7AN" },
  { country: "Hong Kong", address: "Unit 406B, Mirror Tower, 61 Mody Road, Tsim Sha Tsui East, Kowloon" },
  { country: "Armenia", address: "26/4 Saryan Street, 0002, Yerevan" },
];

const linkClass = "w-fit text-[14px] text-foreground/70 transition-colors hover:text-foreground";
const socialClass =
  "flex h-8 w-8 items-center justify-center rounded-full border border-border text-foreground/60 transition-colors duration-200 hover:border-foreground/40 hover:text-foreground";

const SiteFooter = () => {
  return (
    <footer className="border-t border-border/60 bg-secondary/40 text-foreground">
      <div className="container">
        <div className="mx-auto max-w-[1200px] py-[36px] md:py-[72px]">
          <div className="grid grid-cols-2 gap-x-6 gap-y-8 text-left md:grid-cols-2 md:text-left lg:grid-cols-[1.4fr_0.8fr_0.8fr_1.1fr] lg:gap-x-12">
            {/* Column 1 — Brand */}
            <div className="col-span-2 flex min-w-0 flex-col items-center text-center md:col-span-2 md:items-start md:text-left lg:col-span-1">
              <Link to="/" aria-label="Yootelco home" className="flex w-fit items-center">
                <img src={yootelcoLogo.url} alt="Yootelco logo" className="h-7 w-auto" />
              </Link>
              <p className="mt-[12px] max-w-[340px] text-[13px] leading-[1.6] text-muted-foreground">
                Enterprise telecom messaging platform delivering reliable A2P SMS, OTP authentication, and voice services worldwide.
              </p>
              <div className="mt-[12px] space-y-[4px] text-[13.5px]">
                <a href="mailto:info@yootelco.com" className="block text-foreground/75 transition-colors hover:text-foreground">
                  info@yootelco.com
                </a>
                <a href="tel:+442070999469" className="block text-foreground/75 transition-colors hover:text-foreground">
                  +44 207 0999469
                </a>
              </div>
              <div className="mt-[14px] flex items-center justify-center gap-[10px] md:justify-start">
                <a href="https://www.facebook.com/yootelco" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className={socialClass}>
                  <Facebook className="h-[14px] w-[14px]" strokeWidth={2} fill="currentColor" aria-hidden="true" />
                </a>
                <a href="https://www.linkedin.com/company/77003556/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className={socialClass}>
                  <Linkedin className="h-[14px] w-[14px]" strokeWidth={2} fill="currentColor" aria-hidden="true" />
                </a>
                <a href="https://www.instagram.com/yootelco/?utm_medium=copy_link" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className={socialClass}>
                  <Instagram className="h-[14px] w-[14px]" strokeWidth={2} aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* Column 2 — Solutions */}
            <div className="flex min-w-0 flex-col items-start">
              <h3 className="text-[13px] font-normal text-muted-foreground">Solutions</h3>
              <nav className="mt-[12px] flex flex-col items-start gap-[10px]">
                {solutionsLinks.map((item) => (
                  <Link key={item.label} to={item.to} className={linkClass}>
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Column 3 — Company */}
            <div className="flex min-w-0 flex-col items-start">
              <h3 className="text-[13px] font-normal text-muted-foreground">Company</h3>
              <nav className="mt-[12px] flex flex-col items-start gap-[10px]">
                {companyLinks.map((item) =>
                  item.external ? (
                    <a key={item.label} href={item.to} target="_blank" rel="noopener noreferrer" className={linkClass}>
                      {item.label}
                    </a>
                  ) : (
                    <Link key={item.label} to={item.to} className={linkClass}>
                      {item.label}
                    </Link>
                  )
                )}
              </nav>
            </div>

            {/* Column 4 — Offices */}
            <div className="col-span-2 flex min-w-0 flex-col items-start md:col-span-2 lg:col-span-1">
              <h3 className="text-[13px] font-normal text-muted-foreground">Our Offices</h3>
              <ul className="mt-[12px] w-full space-y-[10px]">
                {offices.map((loc) => (
                  <li key={loc.country} className="min-w-0">
                    <p className="text-[13px] font-bold text-foreground">{loc.country}</p>
                    <p className="mt-[2px] text-[12px] leading-[1.5] text-muted-foreground">{loc.address}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-[24px] flex flex-col items-center gap-2 border-t border-border pt-[16px] text-center text-[13px] text-muted-foreground md:mt-[40px] md:flex-row md:items-center md:justify-between md:gap-3 md:pt-[20px] md:text-left">
            <p>© 2026 Yootelco. All rights reserved.</p>
            <div className="flex items-center justify-center gap-[28px]">
              <Link to="/terms" className="transition-colors hover:text-foreground">Terms</Link>
              <Link to="/privacy-policy" className="transition-colors hover:text-foreground">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;